import { Injector, StaticProvider } from '@angular/core';
import { Observable, OperatorFunction } from 'rxjs';

import {
  GraphBuilderContext,
  GraphInputDefinition,
  GraphNodeBuilder,
  GraphNodeInputDefinitionList,
  GraphNodeInputList,
  IGraphBuilder,
  OperatorNodeDefinition,
} from './graph-builder.types';

/**
 * Abstract base class for creating input observables from
 * definitions.
 */
export abstract class AbstractGraphBuilder
  implements GraphNodeBuilder, IGraphBuilder
{
  /**
   * Chain of `GraphNodeBuilder`s
   */
  protected abstract get chain(): GraphNodeBuilder[];

  /**
   * @param providers
   * Adds additional providers
   *
   * @returns
   * An `Injector` to use to resolve DI tokens.
   */
  protected abstract getInjector(providers?: StaticProvider[]): Injector;

  parseOperator(
    def: OperatorNodeDefinition,
    ctx: GraphBuilderContext,
    rootFactory: IGraphBuilder & GraphNodeBuilder = this,
  ): OperatorFunction<any, any> | undefined {
    for (const item of this.chain) {
      // Check if the chain item provides the createPipeOperator function
      if (item.parseOperator) {
        const operator = item.parseOperator(def, ctx, rootFactory);
        // If an operator was successfully created, we want to return it
        if (operator) {
          return operator;
        }
      }
    }
    return undefined;
  }

  parseSource(
    def: GraphInputDefinition,
    ctx: GraphBuilderContext,
    rootFactory: IGraphBuilder & GraphNodeBuilder = this,
  ): Observable<any> | undefined {
    for (const item of this.chain) {
      // Check if the chain item provides the parseSource function
      if (item.parseSource) {
        const source = item.parseSource(def, ctx, rootFactory);
        // If a source was successfully created, we want to return it
        if (source) {
          return source;
        }
      }
    }
    return undefined;
  }

  parse(
    def: GraphInputDefinition,
    ctx: GraphBuilderContext,
  ): Observable<any> | undefined {
    return this._parse(def, ctx);
  }

  private _parse(def: GraphInputDefinition, ctx: GraphBuilderContext) {
    // First, we create the source
    let source$: Observable<unknown> | undefined;
    try {
      source$ = this.parseSource(def, ctx);
      // Only proceed if a source was created
      if (!source$) {
        console.warn('No factory for node', def.name);
        return undefined;
      }
    } catch (error) {
      console.warn(`Error creating node`, def, error);
      return undefined;
    }

    // Then, create the pipe if defined
    const operations: OperatorFunction<unknown, unknown>[] = [];
    if (Array.isArray(def.pipe)) {
      for (const pipeDef of def.pipe) {
        try {
          const operator = this.parseOperator(pipeDef, ctx);

          // If no operator was created, abort source creation
          if (!operator) {
            console.warn('No factory for pipe operator', pipeDef.operator);
            return undefined;
          }

          operations.push(operator);
        } catch (error) {
          // If operator creation failed, abort source creation
          console.warn('Error creating pipeable operator', pipeDef, error);
          return undefined;
        }
      }
    }

    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    return source$.pipe(...operations);
  }

  /**
   * Parses a definition object to an observable source.
   */
  buildInputs<T extends Record<string, any>>(
    def: T,
    providers: StaticProvider[],
  ): GraphNodeInputList<T>;
  buildInputs(
    def: GraphNodeInputDefinitionList,
    providers: StaticProvider[],
  ): GraphNodeInputList {
    const ctx = this.prepareContext(providers);

    return Object.keys(def).reduce((acc, cur) => {
      const sources = this._parse(def[cur], ctx);

      return sources ? { ...acc, [cur]: sources } : acc;
    }, {});
  }

  prepareDefinition<T>(providers?: StaticProvider[]): T | undefined;
  prepareDefinition(
    providers?: StaticProvider[],
  ): GraphNodeInputDefinitionList | undefined;
  prepareDefinition(
    providers?: StaticProvider[],
  ): GraphNodeInputDefinitionList | undefined {
    const ctx = this.prepareContext(providers);
    return this.createFakeDefinitions(ctx);
  }

  createFakeDefinitions(
    ctx: GraphBuilderContext,
  ): GraphNodeInputDefinitionList | undefined {
    for (const item of this.chain) {
      if (item.createFakeDefinitions) {
        const definitions = item.createFakeDefinitions(ctx);
        if (definitions) {
          return definitions;
        }
      }
    }

    return undefined;
  }

  /**
   * Prepares the context to be passed to builders.
   *
   * @param providers Additional providers
   *
   * @returns
   * A context to pass to builders
   */
  private prepareContext(
    providers: StaticProvider[] = [],
  ): GraphBuilderContext {
    const injector = this.getInjector(providers);

    return { injector };
  }
}
